/**
 * StructuredData — the JSON-LD blocks for the marketing site (s216, SEO phase 1 item 7).
 *
 * Three graphs, each answering a different question a search engine asks:
 *   - `Organization`        — who publishes this site
 *   - `SoftwareApplication` — what the product is
 *   - `FAQPage`             — the "how it works" steps, phrased as questions
 *
 * 🔴 NO `offers` GRAPH (D-217.3, ruled s217). The prices came down from the visible page, and
 * structured data is a public surface exactly as the page is — arguably more so, because a crawler
 * reads it whether or not anyone visits. The original shared-array design was right and is the
 * reason this was a one-line removal: the prices lived in `PRICING_TIERS` rather than as literals
 * here, so there was one place to take them from. That import is gone.
 *
 * 🔴 Structured data that contradicts the visible page is a manual-action risk with Google, not
 * just a lost rich result. That cuts BOTH ways: publishing an `Offer` for a price the page no
 * longer shows is the same defect as the drift the shared array was built to prevent.
 *
 * 🔴 The FAQ questions are phrased FROM the existing step copy and add no new claims. The answers
 * are the step bodies verbatim. Nothing here states a benefit the page does not already state —
 * MARKETING-TRUTH-1 applies to structured data exactly as it applies to visible copy, and a
 * sourced-claim rule that a crawler can bypass is not a rule.
 */
import { PRODUCT_NAME } from '@shared/brand';
import { SITE_ORIGIN, absoluteUrl } from '@shared/constants/public-routes';
import { HOW_IT_WORKS } from './how-it-works-data';

/** Renders one JSON-LD script tag. */
function JsonLd({ data }: { data: unknown }) {
  return (
    <script
      type="application/ld+json"
      // JSON.stringify output is inserted as text content, not parsed as HTML. The `<` in any
      // string value is escaped below so a stray "</script>" in copy cannot close the tag.
      dangerouslySetInnerHTML={{ __html: JSON.stringify(data).replace(/</g, '\\u003c') }}
    />
  );
}

const ORGANIZATION = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: PRODUCT_NAME,
  url: `${SITE_ORIGIN}/`,
  logo: `${SITE_ORIGIN}/favicon.svg`,
  areaServed: 'CA',
  contactPoint: {
    '@type': 'ContactPoint',
    contactType: 'customer support',
    url: absoluteUrl('/contact'),
  },
};

function softwareApplication() {
  return {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: PRODUCT_NAME,
    url: `${SITE_ORIGIN}/`,
    applicationCategory: 'BusinessApplication',
    operatingSystem: 'Web',
  };
}

function faqPage() {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: HOW_IT_WORKS.filter((step) => step.body).map((step) => ({
      '@type': 'Question',
      name: step.question,
      acceptedAnswer: { '@type': 'Answer', text: step.body },
    })),
  };
}

/** All three graphs. Mounted once, on the home page. */
export function StructuredData() {
  return (
    <>
      <JsonLd data={ORGANIZATION} />
      <JsonLd data={softwareApplication()} />
      <JsonLd data={faqPage()} />
    </>
  );
}

/** Organization only — for the secondary marketing pages. */
export function OrganizationStructuredData() {
  return <JsonLd data={ORGANIZATION} />;
}
